import * as ActionTypes from 'state/types'
import * as Actions from 'state/actions'
import ReconnectingWebSocket from 'reconnecting-websocket'
import * as ArrayUtil from 'shared/utils/array'

const CONNECTING_READY_STATE = 0
const OPEN_READY_STATE = 1
const CLOSING_READY_STATE = 2
const CLOSED_READY_STATE = 3
const PING_ID_START = 1000
const busyTypingTimeoutIds = {}

const sockets = {
  collection: [],

  each: (cb) => {
    // console.info('Looping the collection', sockets.collection)
    sockets.collection.forEach((s) => {
      // console.info('DOing a callback on every connection')
      cb(s)
    })
  },

  add: (organisationId, socket) => {
    // console.info('Adding connection for agentId', agentId, sockets.collection)
    if (sockets.collection.find(s => s.organisationId === organisationId)) {
      console.error('Adding socket for organisation that already exists', organisationId)
      return
    }
    sockets.collection.push({
      organisationId,
      socket,
      pingId: PING_ID_START,
      usedPingId: null
    })
  },

  contains: (organisationId) => {
    if (sockets.collection.find(s => s.organisationId === organisationId)) {
      return true
    }
    return false
  },

  remove: (organisationId) => {
    const idx = sockets.collection.findIndex(s => s.organisationId === organisationId)
    if (idx === -1) {
      return
    }
    // console.info('Removing connection for agentId', agentId, idx)
    sockets.collection.splice(idx, 1)
  },

  clear: () => {
    // console.info('Clear all connections')
    sockets.collection = []
  },

  with: (organisationId) => {
    // console.info('FInd socket with organisationId', organisationId)
    const item = sockets.collection.find(s => s.organisationId === organisationId)
    if (!item) {
      return null
    }

    return item.socket
  },

  find: (organisationId) => {
    // console.info('FInd socket with organisationId', organisationId)
    const item = sockets.collection.find(s => s.organisationId === organisationId)
    if (!item) {
      return null
    }

    return item
  },

  empty: () => !sockets.collection.length,

  updateKey: ({organisationId, key, nextValue}) => {
    const index = ArrayUtil.indexOf(sockets.collection, s => s.organisationId === organisationId)

    if (index > -1) {
      sockets.collection[index][key] = nextValue
    }
  }
}

const pingSockets = () => {
  setInterval(() => {

    sockets.each(({socket, pingId, usedPingId, organisationId}) => {
      if (!isSocketConnectionOpen(socket)) {
        //dispatch actions depends on status

        switch (socket.readyState) {
          case CONNECTING_READY_STATE: {
            console.warn('socket is connecting, organisationId', organisationId)
            break
          }

          case CLOSING_READY_STATE: {
            console.warn('socket is closing, organisationId', organisationId)
            break
          }

          case CLOSED_READY_STATE: {
            console.warn('socket is closed, organisationId', organisationId)
            break
          }
        }

        sockets.updateKey({
          organisationId,
          key: 'usedPingId',
          nextValue: null
        })

        return
      }

      if (pingId === usedPingId) {
        //during the 5 secs we didn't receive a pong reply for this pingId
        console.warn('during the 5 secs we didn\'t receive a pong reply for this pingId')
        return
      }

      const enveloppe = {
        type: 'ping',
        payload: {
          to: pingId
        }
      }

      socket.send(JSON.stringify(enveloppe))

      sockets.updateKey({
        organisationId,
        key: 'usedPingId',
        nextValue: pingId
      })
    })

  }, 5000)
}

const handlePongMessage = ({payload, organisationId}) => {
  // console.log('Received PONG message with payload, ', payload)
  const {replyTo} = payload

  const socketItem = sockets.find(organisationId)
  if (!socketItem) {
    console.warn(
      'handlePongMessage :: Cannot find the socket connection for organisationId ',
      organisationId
    )

    return
  }
  if (socketItem.pingId !== replyTo) {
    console.warn('handlePongMessage :: Mismatched pingId with pong reply')

    return
  }

  sockets.updateKey({
    organisationId,
    key: 'pingId',
    nextValue: ++socketItem.pingId
  })
}

const isSocketConnectionOpen = socket => {
  if (!socket) return false
  if (window.navigator.onLine === false) {
    console.warn('socketMiddleware :: the internet connection is offline')
    return false
  }

  return socket.readyState === OPEN_READY_STATE
}

const handleOpen = (socket, store, endpoints, socketCounter) => () => {

  const token = store.getState().session.token

  if (!isSocketConnectionOpen(socket)) return

  setTimeout(() => openNextSocketConnection(store, endpoints, socketCounter), 100)
  // Send auth
  socket.send(JSON.stringify({
    type: 'authenticate',
    payload: {
      token
    }
  }))
}

const handleError = (socket, store, organisationId) => (err) => {
  console.error('Error using websocket', organisationId, err)
  if (socket.retryCount === 19) {
    console.error('Failed to establish a connection after 19 attempt(s)')
    store.dispatch(Actions.socketReconnectionError())
  }
}

const handleMessage = (socket, store, organisationId) => evt => {
  try {
    const state = store.getState()
    const {profile} = state.session
    const {
      type,
      payload
    } = JSON.parse(evt.data)

    switch (type) {
      case 'activities.created': {
        const {interaction} = state.selected
        if (interaction && interaction.interactionId === payload.interactionId) {
          store.dispatch(Actions.activityReceived(payload))
        }
        break
      }

      case 'interactions.created':
        store.dispatch(Actions.newInteractionReceived(payload, profile))
        break

      case 'interactions.updated':
        store.dispatch(Actions.updatedInteractionReceived(payload, profile))
        break

      case 'interactions.noticed':
        store.dispatch(Actions.noticedInteractionReceived(payload))
        break

      case 'brains.paused':
        store.dispatch(Actions.receiveUpdatedPause(payload))
        break

      case 'brains.resumed':
        store.dispatch(Actions.receiveDeletedPause(payload))
        break

      case 'interaction.deleted': {
        store.dispatch(Actions.receiveDeletedInteraction(payload))
        break
      }

      case 'logs.error':
      case 'logs.info':
        // store.dispatch(Actions.receiveLogEntry(payload))
        break

      case 'pong': {
        handlePongMessage({payload, organisationId})
        break
      }

      case 'ping': {
        socket.send(JSON.stringify({
          type: 'pong'
        }))
        break
      }

      case 'activity.user.typing': {
        const {
          threadId
        } = payload

        clearTimeout(busyTypingTimeoutIds[threadId])

        store.dispatch(Actions.activityStartReceived(payload))
        const busyTypingTimeoutId = setTimeout(() => store.dispatch(Actions.removeBusyTyping(payload)), 10000)
        busyTypingTimeoutIds[threadId] = busyTypingTimeoutId

        break
      }
      case 'activity.bot.start': {
        store.dispatch(Actions.activityStartReceived(payload))
        break
      }
      case 'activity.bot.stop': {
        store.dispatch(Actions.removeBusyTyping(payload))
        break
      }

      case 'inbox.updated': {
        store.dispatch(Actions.receiveUpdatedInbox(payload))
        break
      }
      case 'counters.updated': {
        console.log('**', payload)
        if(payload.type === 'interaction.assign' ||
          payload.type === 'interaction.archive' ||
          payload.type === 'interaction.remove'
        ){
          const userId = store.getState().session.profile.userId
          if(payload.userId !== userId){
            delete payload.counters.assigned
            delete payload.counters.inboxes
          }
        }
        store.dispatch(Actions.countersUpdated(payload))
        break
      }
    }
  } catch (err) {
    console.error('Error while handling a socket message', err)
  }
}


const openNextSocketConnection = (store, endpoints, socketCounter = 0) => {

  if (endpoints.length - 1 < socketCounter) return

  const {
    organisationId,
    socketUrl
  } = endpoints[socketCounter]

  if (sockets.with(organisationId)) {
    return
  }

  const options = {
    //maxReconnectionDelay: 10000,
    minReconnectionDelay: 1000 + Math.random() * 4000,
    reconnectionDelayGrowFactor: 1.3,
    // minUptime: 5000,
    connectionTimeout: 6000,
    maxRetries: 20
    // maxEnqueuedMessages: 10,
    // startClosed: false,
    // debug: false
  }

  const socket = new ReconnectingWebSocket(socketUrl, [], options)
  socket.onmessage = handleMessage(socket, store, organisationId)
  socket.onopen = handleOpen(socket, store, endpoints, socketCounter + 1)
  socket.onerror = handleError(socket, store, organisationId)
  sockets.add(organisationId, socket)
}

export const socketMiddleware = store => next => action => {

  switch (action.type) {
    case `${ActionTypes.SOCKET_INFO_REQUEST}_SUCCESS`: {

      const endpoints = action.response.payload.endpoints
      try {
        openNextSocketConnection(store, endpoints)

        pingSockets()
      } catch (err) {
        console.error('Error, failed to connect with websocket', err)
      }
    }
      break

    case ActionTypes.SEND_ACTIVITY: {

      // console.info(`Handling event`, action.type)
      if (sockets.empty()) {
        console.error('Error, no sockets to send message')
        break
      }

      const {selected, session, organisation} = store.getState()

      const {
        speech
      } = action

      const {
        channelId,
        channelName,
        threadId
      } = selected.interaction.threads.find(({active}) => active)

      const {
        picture,
        email,
        firstName,
        lastName,
        language,
        gender
      } = session.profile || {}

      const organisationId = organisation.active?.organisationId

      const name = firstName ? `${firstName} ${lastName}` : 'support'

      const params = {
        agentId: selected.interaction.agentId,
        organisationId,
        channelId,
        channelName,
        threadId,
        speech,
        originator: {
          userId: session.profile.userId,
          name,
          role: 'user',
          profile: {
            fullName: (firstName && lastName) ? `${firstName} ${lastName}` : undefined,
            firstName: firstName || undefined,
            lastName: lastName || undefined,
            locale: language || undefined,
            gender: gender || undefined,
            picture: picture || undefined,
            email: email || undefined
          }
        }
      }

      const enveloppe = {
        type: 'message.send',
        payload: params
      }

      const {
        participants
      } = selected.interaction

      const externalParticipant = participants.find(participant => participant?.role === 'external')

      const userId = externalParticipant && (externalParticipant.handles && externalParticipant.handles[0] ? externalParticipant.handles[0].userId : externalParticipant.userId)

      if (!userId) {
        window.alert('No external user reply to')
        return
      }

      enveloppe.payload.user = {
        userId
      }

      const socket = sockets.with(organisationId)
      enveloppe.payload.tempId = Date.now()
      if (!isSocketConnectionOpen(socket)) {
        enveloppe.payload.failCount = 1
        enveloppe.payload.organisationId = organisationId
        enveloppe.payload.tempId = Date.now()
        store.dispatch(Actions.addMessage(enveloppe.payload))
        store.dispatch(Actions.resendMessage(enveloppe.payload))
        break
      }

      store.dispatch(Actions.addMessage(enveloppe.payload))
      socket.send(JSON.stringify(enveloppe))
      break
    }

    case ActionTypes.RESEND_ACTIVITY: {
      const {
        payload
      } = action
      const enveloppe = {
        type: 'message.send',
        payload: payload
      }
      const socket = sockets.with(payload.organisationId)
      if(payload.failCount === 1) {
        enveloppe.payload.failed = false
        store.dispatch(Actions.updateActivity(payload))
      }
      payload.failCount++

      if (!isSocketConnectionOpen(socket)) {
        if (payload.failCount >= 10) {
          enveloppe.payload.failed = true
          payload.failCount = 1
          store.dispatch(Actions.updateActivity(payload))
        } else {
          setTimeout(() => store.dispatch(Actions.resendMessage(payload)), 500)
        }
        break
      }

      socket.send(JSON.stringify(enveloppe))
      break
    }

    case ActionTypes.SEND_ACTIVITY_START: {
      const {organisation, session, selected} = store.getState()
      const organisationId = organisation.active?.organisationId
      const thread = selected.interaction?.threads.find(thread => thread.active === true)
      const agentId = selected.interaction?.agentId

      if (sockets.empty() || !thread || !agentId) {
        console.error('Error, no sockets to send message or no active thread')
        break
      }

      const threadId = thread.threadId
      const socket = sockets.with(organisationId)

      const {
        picture,
        email,
        firstName,
        lastName,
        language,
        gender,
        userId
      } = session.profile || {}

      const enveloppe = {
        type: 'agent.typing',
        payload: {
          organisationId,
          agentId,
          threadId,
          originator: {
            userId: userId,
            name: firstName ? `${firstName} ${lastName}` : 'support',
            role: 'user',
            profile: {
              fullName: (firstName && lastName) ? `${firstName} ${lastName}` : undefined,
              firstName: firstName || undefined,
              lastName: lastName || undefined,
              locale: language || undefined,
              gender: gender || undefined,
              picture: picture || undefined,
              email: email || undefined
            }
          }
        }
      }

      socket.send(JSON.stringify(enveloppe))
      break
    }

    //Interaction has been noticed
    case ActionTypes.NOTICE_INTERACTION: {

      if (sockets.empty()) {
        console.error('Error, no sockets to send notice')
        break
      }

      const { selected, organisation } = store.getState()
      const { interaction } = selected

      if (!interaction?.isUnnoticed) break

      const organisationId = organisation.active?.organisationId
      const socket = sockets.with(organisationId)

      if (!isSocketConnectionOpen(socket)) break

      socket.send(JSON.stringify({
        type: 'interaction.noticed',
        payload: {
          interactionId: interaction?.interactionId
        }
      }))

      break
    }
  }

  return next(action)

}
